/**
 * AnalyticsComparisonDashboard – Optimized
 *
 * This is now a thin shell that:
 * 1. Owns the global filter state (date range, regular/contractual toggle, active tab)
 * 2. Fetches ONLY the shared/core data via useAnalyticsCoreData (RTK Query cached)
 * 3. Renders the active tab component, which fetches its own tab-specific data
 *
 * Key improvements:
 * - Eliminated 3 manual attendanceApi calls (replaced by RTK Query hooks)
 * - Removed duplicate historicalKVDataTop query (page-1 reused for top-10 KVs)
 * - Removed 8 useEffect sync hooks + ~15 useState variables (useMemo used directly)
 * - Split from 2,240 lines into ~200-line shell + 4 focused tab components
 * - Tab-specific queries only fire when the tab is mounted (no skip needed)
 * - Math.random() mock data removed — trends computed from real period comparison
 * - Added proper ARIA roles for tab navigation
 */

import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  RefreshCw,
  Activity,
  MapPin,
  Building2,
  Users,
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';

import { OverviewTab, RegionsTab, SchoolsTab, EmployeesTab } from './tabs';
import { useAnalyticsCoreData, type TabType } from './useAnalyticsData';

const TABS: { id: TabType; label: string; icon: React.FC<{ className?: string }> }[] = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'regions', label: 'Region Comparison', icon: MapPin },
  { id: 'schools', label: 'KV Schools', icon: Building2 },
  { id: 'employees', label: 'Employee Trends', icon: Users },
];

const AnalyticsComparisonDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const carouselImages = [img1];
  const customFromDateRef = useRef<HTMLInputElement>(null);

  // ── Global filter state ──
  const activeTab = (searchParams.get('tab') as TabType) || 'overview';
  const setActiveTab = (tab: TabType) => setSearchParams({ tab });

  const [dateRange, setDateRange] = useState<number>(30);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [employeeApiType, setEmployeeApiType] = useState<'regular' | 'contractual'>('regular');

  // ── Core data hook (RTK Query cached) ──
  const {
    computedDateRange,
    dailyAttendanceSummary,
    totalKVISData,
    processedROData,
    topRegionTrends,
    regions,
    metrics,
    isCoreLoading,
  } = useAnalyticsCoreData(dateRange, isCustomDateRange, customFromDate, customToDate, employeeApiType);

  // Auto-open date picker when switching to custom range
  useEffect(() => {
    if (isCustomDateRange && customFromDateRef.current) {
      setTimeout(() => customFromDateRef.current?.showPicker?.(), 100);
    }
  }, [isCustomDateRange]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 -pt-2 -sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* ── Page Header ── */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] flex items-center gap-2">
              <BarChart3 className="w-7 h-7" />
              Analytics Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Compare attendance trends across regions, schools, and employees
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Regular / Contractual Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1" role="radiogroup" aria-label="Staff type">
              {(['regular', 'contractual'] as const).map(type => (
                <button
                  key={type}
                  role="radio"
                  aria-checked={employeeApiType === type}
                  onClick={() => setEmployeeApiType(type)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    employeeApiType === type
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {type === 'regular' ? 'Regular' : 'Contractual'}
                </button>
              ))}
            </div>

            {/* Date range selector */}
            {!isCustomDateRange ? (
              <select
                value={dateRange}
                onChange={e => {
                  const val = e.target.value;
                  if (val === 'custom') {
                    setIsCustomDateRange(true);
                    const today = new Date();
                    const ago = new Date();
                    ago.setDate(ago.getDate() - 30);
                    setCustomToDate(today.toISOString().split('T')[0]);
                    setCustomFromDate(ago.toISOString().split('T')[0]);
                  } else {
                    setDateRange(Number(val));
                  }
                }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value={0}>Today</option>
                <option value={7}>Last 7 Days</option>
                <option value={14}>Last 14 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={60}>Last 60 Days</option>
                <option value={90}>Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  ref={customFromDateRef}
                  type="date"
                  value={customFromDate}
                  onChange={e => setCustomFromDate(e.target.value)}
                  onClick={e => (e.currentTarget as HTMLInputElement).showPicker?.()}
                  max={customToDate}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white cursor-pointer"
                  aria-label="From date"
                />
                <span className="text-gray-400">→</span>
                <input
                  type="date"
                  value={customToDate}
                  onChange={e => setCustomToDate(e.target.value)}
                  onClick={e => (e.currentTarget as HTMLInputElement).showPicker?.()}
                  min={customFromDate}
                  max={new Date().toISOString().split('T')[0]}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white cursor-pointer"
                  aria-label="To date"
                />
                <button
                  onClick={() => {
                    setIsCustomDateRange(false);
                    setDateRange(30);
                  }}
                  className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                  title="Back to preset ranges"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Refresh indicator */}
            {isCoreLoading && (
              <div className="p-2">
                <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* ── Tab Navigation (ARIA compliant) ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-1" role="tablist" aria-label="Analytics sections">
          <div className="flex overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`tabpanel-${tab.id}`}
                id={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab Content ── */}
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {isCoreLoading && !metrics ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
                <p className="text-gray-500">Loading analytics data...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <OverviewTab
                  metrics={metrics}
                  dailyAttendanceSummary={dailyAttendanceSummary}
                  totalKVISData={totalKVISData}
                  topRegionTrends={topRegionTrends}
                  heatmapData={processedROData.heatmap}
                  topRegions={processedROData.topRegions}
                  bottomRegions={processedROData.bottomRegions}
                  employeeApiType={employeeApiType}
                />
              )}
              {activeTab === 'regions' && (
                <RegionsTab
                  computedDateRange={computedDateRange}
                  heatmapData={processedROData.heatmap}
                  employeeApiType={employeeApiType}
                />
              )}
              {activeTab === 'schools' && (
                <SchoolsTab
                  computedDateRange={computedDateRange}
                  employeeApiType={employeeApiType}
                  regions={regions}
                />
              )}
              {activeTab === 'employees' && (
                <EmployeesTab
                  computedDateRange={computedDateRange}
                  employeeApiType={employeeApiType}
                />
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AnalyticsComparisonDashboard;
